import React from 'react';
import { UserCheck, LogIn, Building2, Users, Package, Truck, CheckCircle, Loader2 } from 'lucide-react';
import KPICard from '../components/KPICard';
import DataTable from '../components/DataTable';
import QueryInfoButton from '../components/QueryInfoButton';
import {
  useTenantAdoptionToday,
  useTenantAdoptionSummary,
  useCardMetricsToday,
} from '../hooks/useReports';
import { formatNumber } from '../utils/formatters';
import { TENANT_ADOPTION_QUERIES, CARD_METRICS_QUERIES } from '../constants/queries';

export default function Dashboard() {
  const { data: todayData, isLoading: todayLoading } = useTenantAdoptionToday();
  const { data: summaryData, isLoading: summaryLoading } = useTenantAdoptionSummary();
  const { data: cardData, isLoading: cardLoading } = useCardMetricsToday();

  const summary = summaryData?.data?.[0] || {};

  const todayTotals = React.useMemo(() => {
    if (!todayData?.data) return { onboarded: 0, logins: 0, totalMembers: 0 };
    return todayData.data.reduce(
      (acc, row) => ({
        onboarded: acc.onboarded + parseInt(row.new_adopted_consumers || 0),
        logins: acc.logins + parseInt(row.count_logins || 0),
        totalMembers: acc.totalMembers + parseInt(row.total_members || 0),
      }),
      { onboarded: 0, logins: 0, totalMembers: 0 }
    );
  }, [todayData]);

  const cardTotals = React.useMemo(() => {
    if (!cardData?.data) return { ordered: 0, shipped: 0, activated: 0 };
    return cardData.data.reduce(
      (acc, row) => ({
        ordered: acc.ordered + parseInt(row.cards_ordered_count || 0),
        shipped: acc.shipped + parseInt(row.cards_shipped_count || 0),
        activated: acc.activated + parseInt(row.cards_activated_count || 0),
      }),
      { ordered: 0, shipped: 0, activated: 0 }
    );
  }, [cardData]);

  const isLoading = todayLoading || summaryLoading || cardLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin" />
          <p className="text-gray-500 text-lg">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  const kpis = [
    {
      title: 'Total Members Onboarded',
      value: summary.total_members_onboarded,
      icon: UserCheck,
      format: 'compact',
    },
    {
      title: 'Total Members Logged In',
      value: summary.total_members_logged_in,
      icon: LogIn,
      format: 'compact',
    },
    {
      title: 'Active Clients',
      value: summary.total_tenants,
      icon: Building2,
      format: 'number',
    },
    {
      title: "Today's Onboarded",
      value: todayTotals.onboarded,
      icon: Users,
      format: 'number',
    },
    {
      title: "Today's Cards Activated",
      value: cardTotals.activated,
      icon: CheckCircle,
      format: 'number',
    },
  ];

  const tenantColumns = [
    { key: 'tenant_name', label: 'Tenant', sortable: true },
    { key: 'new_adopted_consumers', label: 'New Onboarded', sortable: true, format: 'number' },
    { key: 'count_logins', label: 'Logins', sortable: true, format: 'number' },
    { key: 'total_adopted_consumers', label: 'Total Onboarded', sortable: true, format: 'number' },
    { key: 'total_members', label: 'Total Members', sortable: true, format: 'number' },
    { key: 'percentage_onboarded', label: '% Onboarded', sortable: true, format: 'percent' },
  ];

  const cardColumns = [
    { key: 'tenant_name', label: 'Tenant', sortable: true },
    { key: 'cards_ordered_count', label: 'Cards Ordered', sortable: true, format: 'number' },
    { key: 'cards_shipped_count', label: 'Cards Shipped', sortable: true, format: 'number' },
    { key: 'cards_activated_count', label: 'Cards Activated', sortable: true, format: 'number' },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Platform Overview</h2>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">
            Member onboarding and login metrics across all tenants
          </p>
        </div>
        <QueryInfoButton
          title="Dashboard Data Sources"
          queries={[
            TENANT_ADOPTION_QUERIES.summary,
            TENANT_ADOPTION_QUERIES.today,
            CARD_METRICS_QUERIES.today,
          ]}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
        {kpis.map((kpi, index) => (
          <KPICard key={index} {...kpi} loading={summaryLoading || todayLoading || cardLoading} />
        ))}
      </div>

      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
          <div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Today's Tenant Activity</h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Last Refreshed On: {new Date().toLocaleString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 sm:gap-6 text-sm">
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold text-green-600">{formatNumber(todayTotals.onboarded)}</div>
              <div className="text-gray-500 text-[10px] sm:text-sm">New Onboarded</div>
            </div>
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold text-amber-600">{formatNumber(todayTotals.logins)}</div>
              <div className="text-gray-500 text-[10px] sm:text-sm">Total Logins</div>
            </div>
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold text-purple-600">{formatNumber(todayTotals.totalMembers)}</div>
              <div className="text-gray-500 text-[10px] sm:text-sm">Total Members</div>
            </div>
          </div>
        </div>

        <DataTable
          data={todayData?.data}
          columns={tenantColumns}
          loading={todayLoading}
          emptyMessage="No tenant activity data available for today"
        />
      </div>

      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
          <div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Today's Card Activity</h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Card orders, shipments, and activations
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 sm:gap-6 text-sm">
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold text-orange-600">{formatNumber(cardTotals.ordered)}</div>
              <div className="text-gray-500 text-[10px] sm:text-sm">Cards Ordered</div>
            </div>
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold text-amber-600">{formatNumber(cardTotals.shipped)}</div>
              <div className="text-gray-500 text-[10px] sm:text-sm">Cards Shipped</div>
            </div>
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold text-green-600">{formatNumber(cardTotals.activated)}</div>
              <div className="text-gray-500 text-[10px] sm:text-sm">Cards Activated</div>
            </div>
          </div>
        </div>

        <DataTable
          data={cardData?.data}
          columns={cardColumns}
          loading={cardLoading}
          emptyMessage="No card activity data available for today"
        />
      </div>
    </div>
  );
}
